import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

// 全局只注册一次插件
let registered = false;
if (!registered && typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
  registered = true;
}

// 暴露当前页面的 Lenis 平滑滚动实例，供「回到顶部」等组件复用。
// 必须走 lenis.scrollTo，否则原生 scrollTo 会与 Lenis 的 raf 循环打架、出现抖动。
let activeLenis = null;
export function getLenis() {
  return activeLenis;
}

/**
 * 三卡翻转滚动动画 —— 移植自参考项目 script.js，GSAP 参数原样照搬。
 * 仅选择器改名避免全局冲突：
 *   .sticky→.home-card-sticky / .card-container→.home-card-container
 *   .card→.home-flip-card / #card-N→#home-card-N / .sticky-header h1→.home-card-sticky-header h1
 *
 * 两个动画门参考用模块级 let，React 里必须改用 ref（闭包读写、不触发重渲染，
 * 否则 scrub 会被打断或值被冻结）。
 */
export default function useCardFlipScroll({ stickyRef, containerRef, headerRef }) {
  const gapDoneRef = useRef(false);
  const flipDoneRef = useRef(false);

  useEffect(() => {
    const sticky = stickyRef.current;
    const container = containerRef.current;
    const header = headerRef.current;
    if (!sticky || !container || !header) return undefined;

    gapDoneRef.current = false;
    flipDoneRef.current = false;

    // Lenis 平滑滚动 + 接入 gsap ticker
    const lenis = new Lenis();
    activeLenis = lenis;
    const onScroll = () => ScrollTrigger.update();
    lenis.on('scroll', onScroll);
    const tickerFn = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tickerFn);
    gsap.ticker.lagSmoothing(0);

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      // 移动端：清内联样式、关闭动画（与参考 ≤999px 分支一致）
      mm.add('(max-width: 999px)', () => {
        document
          .querySelectorAll('.home-flip-card, .home-card-container, .home-card-sticky-header h1')
          .forEach((el) => { el.style.cssText = ''; });
        return {};
      });

      // 桌面端：pin + scrub 滚动驱动
      mm.add('(min-width: 1000px)', () => {
        ScrollTrigger.create({
          trigger: sticky,
          start: 'top top',
          end: `+=${window.innerHeight * 4}px`,
          scrub: 1,
          pin: true,
          pinSpacing: true,
          onUpdate: (self) => {
            const progress = self.progress;

            // === stickyHeader：y 位移 + 透明度（0.05–0.3，套 power3.out 缓动）===
            if (progress >= 0.05 && progress <= 0.3) {
              const headerProgress = gsap.utils.mapRange(0.05, 0.3, 0, 1, progress);
              const eased = gsap.parseEase('power3.out')(headerProgress);
              gsap.set(header, {
                y: gsap.utils.mapRange(0, 1, 40, 0, eased),
                opacity: gsap.utils.mapRange(0, 1, 0, 1, eased),
              });
            } else if (progress < 0.05) {
              gsap.set(header, { y: 40, opacity: 0 });
            } else if (progress > 0.3) {
              gsap.set(header, { y: 0, opacity: 1 });
            }

            // === cardContainer 宽度变化（90%→75%）===
            if (progress <= 0.3) {
              gsap.set(container, { width: `${gsap.utils.mapRange(0, 0.3, 90, 75, progress)}%` });
            } else {
              gsap.set(container, { width: '75%' });
            }

            // === Gap 动画：卡片间距 + 圆角（≥0.35 触发，回滚反向）===
            if (progress >= 0.35 && !gapDoneRef.current) {
              gsap.to(container, { gap: '20px', duration: 0.5, ease: 'power3.out' });
              gsap.to(['#home-card-1', '#home-card-2', '#home-card-3'], {
                borderRadius: '20px',
                duration: 0.5,
                ease: 'power3.out',
              });
              gapDoneRef.current = true;
            } else if (progress < 0.35 && gapDoneRef.current) {
              gsap.to(container, { gap: '0px', duration: 0.5, ease: 'power3.out' });
              gsap.to('#home-card-1', { borderRadius: '20px 0 0 20px', duration: 0.5, ease: 'power3.out' });
              gsap.to('#home-card-2', { borderRadius: '0px', duration: 0.5, ease: 'power3.out' });
              gsap.to('#home-card-3', { borderRadius: '0 20px 20px 0', duration: 0.5, ease: 'power3.out' });
              gapDoneRef.current = false;
            }

            // === Flip 动画：卡片翻转 + 位移旋转（≥0.45 触发，回滚反向）===
            if (progress >= 0.45 && !flipDoneRef.current) {
              gsap.to('.home-flip-card', { rotationY: 180, duration: 0.6, ease: 'power3.inOut', stagger: 0.1 });
              gsap.to(['#home-card-1', '#home-card-3'], {
                y: 30,
                rotationZ: (i) => [-15, 15][i],
                duration: 0.6,
                ease: 'power3.inOut',
              });
              flipDoneRef.current = true;
            } else if (progress < 0.45 && flipDoneRef.current) {
              gsap.to('.home-flip-card', { rotationY: 0, duration: 0.6, ease: 'power3.inOut', stagger: -0.1 });
              gsap.to(['#home-card-1', '#home-card-3'], {
                y: 0,
                rotationZ: 0,
                duration: 0.6,
                ease: 'power3.inOut',
              });
              flipDoneRef.current = false;
            }
          },
        });
      });
    }, sticky);

    // resize：仅刷新位置，不重建 ScrollTrigger（避免与 React StrictMode 双 mount 打架）
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => ScrollTrigger.refresh(), 250);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimer);
      ctx.revert();
      gsap.ticker.remove(tickerFn);
      lenis.off('scroll', onScroll);
      lenis.destroy();
      activeLenis = null;
    };
  }, [stickyRef, containerRef, headerRef]);
}
